import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { relativeTime } from '../../shared/lib/format';
import { errorClassMeta } from '../../shared/status/dictionary';
import { Button } from '../../shared/ui/button';
import { ChevronDownIcon } from '../../shared/ui/icons';
import { FactList, FactRow } from '../../shared/ui/fact-list';
import { Page, PageHeader, Section } from '../../shared/ui/layout';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../shared/ui/menu';
import { StatusBadge } from '../../shared/ui/status-badge';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import {
  counted,
  destinationActivityLine,
  duration,
  getDestination,
  useDestinationLiveUpdate,
  type TDestination,
} from '../../entities/destination';
import { usedInKeys } from '../../entities/used-in';
import { EMPTY_RUN_FILTERS, runFiltersToSearch, type TRunShow } from '../../entities/run';
import { DestinationDelete } from '../../features/destination-delete';
import { DestinationProbe } from '../../features/destination-probe';
import {
  DestinationCalling,
  DestinationCli,
  DestinationEndpoint,
  DestinationHeaders,
  DestinationName,
  DestinationSaveConfirm,
  useDestinationSettings,
} from '../../features/destination-settings';
import { DestinationSigning } from '../../features/destination-signing';
import { UsedInFlows } from '../../widgets/used-in-flows';

interface IScreenProps {
  destination: TDestination;
  breadcrumb: ReactNode;
  timeZone?: string;
}

// The window the rollup counted over, on the Flows view of Events: copies are runs' now.
const eventsSearch = (destination: TDestination, show: TRunShow = 'all') => ({
  ...runFiltersToSearch({
    ...EMPTY_RUN_FILTERS,
    minutes: destination.activity.windowHours * 60,
    show,
    destinationId: [destination.id],
  }),
});

const Counted = ({
  destination,
  value,
  status,
}: {
  destination: TDestination;
  value: number;
  status: TRunShow;
}) => {
  const text = counted(value, destination.activity.computedAt);
  if (text === null) return <span className="text-faint">not counted yet</span>;
  if (value === 0) return <>0</>;
  return (
    <Link to="/events" search={eventsSearch(destination, status)} className="hover:underline">
      {text}
    </Link>
  );
};

const Health = ({ destination, now }: { destination: TDestination; now: number }) => {
  const activity = destination.activity;
  const failureClass = activity.lastErrorClass ? errorClassMeta(activity.lastErrorClass) : null;
  // The failure's own answer: the last answer overall may be the 200 that came after it.
  const failure = [
    activity.lastFailureStatus !== null
      ? `answered ${activity.lastFailureStatus}`
      : activity.lastErrorClass?.startsWith('status')
        ? (failureClass?.label ?? null)
        : (failureClass?.label ?? activity.lastErrorClass),
  ].filter(Boolean);

  return (
    <Section
      title="How deliveries are going"
      description={`Over the past ${activity.windowHours} hours. Retry pending and the last tries are live; the totals are recomputed every few minutes.`}
      actions={
        <Link to="/events" search={eventsSearch(destination)} className="text-meta text-accent">
          See its events
        </Link>
      }
    >
      <FactList className="max-w-form-narrow">
        <FactRow label="Delivered" tone="success">
          <Counted destination={destination} value={activity.deliveredInWindow} status="all" />
        </FactRow>
        {/* A failed try waiting for the next one is failing now, not only once it gives up. */}
        <FactRow label="Retry pending" tone={activity.retryingInWindow > 0 ? 'danger' : 'motion'}>
          <Counted destination={destination} value={activity.retryingInWindow} status="problems" />
        </FactRow>
        <FactRow label="Gave up" tone={activity.exhaustedInWindow > 0 ? 'danger' : 'default'}>
          <Counted destination={destination} value={activity.exhaustedInWindow} status="problems" />
        </FactRow>
        <FactRow label="Last delivered">
          {activity.lastSuccessAt
            ? relativeTime(activity.lastSuccessAt, now)
            : activity.computedAt === null
              ? 'not counted yet'
              : 'never'}
        </FactRow>
        {activity.lastFailureAt && (
          <FactRow label="Last failure" tone="danger">
            {relativeTime(activity.lastFailureAt, now)}
            {failure.length > 0 && ` · ${failure.join('')}`}
          </FactRow>
        )}
        <FactRow label="Median answer">
          {activity.p50LatencyMs === null
            ? 'too few deliveries to say'
            : duration(activity.p50LatencyMs)}
        </FactRow>
      </FactList>
    </Section>
  );
};

const DestinationScreen = ({ destination, breadcrumb, timeZone }: IScreenProps) => {
  const settings = useDestinationSettings(destination);
  const [deleting, setDeleting] = useState(false);
  const cli = destination.transport === 'cli';

  const now = useMemo(() => Date.now(), [destination]);

  return (
    <>
      <PageHeader
        width="wide"
        title={destination.name}
        breadcrumbs={breadcrumb}
        description={
          <span className="flex items-center gap-3">
            <StatusBadge vocabulary="destination" value={destination.state} />
            <span>{destinationActivityLine(destination.activity, now)}</span>
          </span>
        }
        actions={
          <>
            {!cli && <DestinationProbe destination={destination} />}
            <Menu>
              <MenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  More <ChevronDownIcon size={12} />
                </Button>
              </MenuTrigger>
              <MenuContent align="end">
                <MenuItem onSelect={() => setDeleting(true)}>Delete this destination</MenuItem>
              </MenuContent>
            </Menu>
            <Button
              size="sm"
              disabled={!settings.canSave}
              waiting={settings.save.isPending || settings.checking}
              waitingLabel="Saving"
              onClick={() => void settings.requestSave()}
            >
              Save changes
            </Button>
          </>
        }
      />

      <Page width="wide">
        <DestinationName settings={settings} />
        <Health destination={destination} now={now} />
        <UsedInFlows
          kind="destination"
          id={destination.id}
          name={destination.name}
          timeZone={timeZone}
          className="max-w-form"
        />
        {cli ? (
          <DestinationCli settings={settings} now={now} />
        ) : (
          <>
            <DestinationEndpoint settings={settings} />
            <DestinationCalling settings={settings} />
            <DestinationSigning settings={settings} timeZone={timeZone} />
            <DestinationHeaders settings={settings} />
          </>
        )}
      </Page>

      <DestinationSaveConfirm settings={settings} />
      <DestinationDelete destination={destination} open={deleting} onOpenChange={setDeleting} />
    </>
  );
};

export const DestinationPage = ({
  destinationId,
  ...rest
}: { destinationId: string } & Omit<IScreenProps, 'destination'>) => {
  // Refetched on return to the tab: "last delivered 4 min ago" must not freeze at mount.
  const destination = useQuery({
    queryKey: ['destination', destinationId],
    queryFn: () => getDestination(destinationId),
    refetchOnWindowFocus: true,
  });
  useDestinationLiveUpdate([
    ['destination', destinationId],
    usedInKeys.one('destination', destinationId),
  ]);

  if (destination.isError) {
    return (
      <div className="p-page">
        <ErrorState error={destination.error} onRetry={() => void destination.refetch()} />
      </div>
    );
  }

  if (!destination.data) {
    return (
      <div className="p-page">
        <LoadingState rows={8} />
      </div>
    );
  }

  // Keyed by id: the unsaved draft inside must not survive a switch to another destination.
  return <DestinationScreen key={destination.data.id} destination={destination.data} {...rest} />;
};
